import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import App from './App'
import { AuthProvider } from './lib/auth/AuthProvider'
import { JurisdictionProvider } from './lib/jurisdiction/JurisdictionProvider'
import { ThemeProvider } from './components/providers/ThemeProvider'
import { ColorThemeProvider } from './components/providers/ColorThemeProvider'
import { CommandPaletteProvider } from './components/ui/command-palette'
import { Toaster } from './components/ui/toaster'
import { THEME_STORAGE_KEY, DEFAULT_THEME, isThemeName } from './lib/themes'

import './index.css'
import './styles/aurora.css'
import './i18n'

// Apply the persisted color theme + light/dark class BEFORE first paint to avoid
// a flash of the default theme. The full reconcile (backend wins for color theme)
// happens inside the providers. Mirrors the client portal's before-paint apply.
{
  try {
    const storedColor = window.localStorage.getItem(THEME_STORAGE_KEY)
    document.documentElement.setAttribute(
      'data-color-theme',
      isThemeName(storedColor) ? storedColor : DEFAULT_THEME,
    )
  } catch {
    document.documentElement.setAttribute('data-color-theme', DEFAULT_THEME)
  }
  try {
    const storedMode = window.localStorage.getItem('sgrc-adminclient-theme') || 'system'
    const isDark =
      storedMode === 'dark' ||
      (storedMode === 'system' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches)
    document.documentElement.classList.toggle('dark', isDark)
    document.documentElement.classList.toggle('light', !isDark)
    // Keep `data-theme` in sync with the `.dark`/`.light` class from first paint
    // so the light/dark mode is applied (and persisted) before React hydrates.
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light')
  } catch {
    /* ignore */
  }
}

/**
 * Global handler for Firefox dynamic-import races (NS_BINDING_ABORTED on
 * route navigation aborts in-flight `import()`s → "error loading dynamically
 * imported module" TypeError). These are noise on aborted navigations; the
 * Suspense fallback already covers the legitimate retry path inside lazyWithRetry.
 * Prevents the unhandledrejection from reaching React's logCapturedError
 * which surfaces it as a bare console.error('Error') in our shard runs.
 */
window.addEventListener('unhandledrejection', (event) => {
  const r: any = event.reason
  const msg = (r?.message || String(r || '')).toLowerCase()
  if (
    msg.includes('dynamically imported module') ||
    msg.includes('importing a module script') ||
    msg.includes('loading css chunk') ||
    msg.includes('loading chunk')
  ) {
    // Swallow — lazyWithRetry handles the active route; aborted prefetches
    // are recoverable on next navigation.
    event.preventDefault()
  }
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <JurisdictionProvider>
            <ThemeProvider defaultTheme="system" storageKey="sgrc-adminclient-theme">
              <ColorThemeProvider>
                <CommandPaletteProvider>
                  <App />
                </CommandPaletteProvider>
                <Toaster />
              </ColorThemeProvider>
            </ThemeProvider>
          </JurisdictionProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
)
